import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Button, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { membersApiSlice } from "state/members/membersApiSlice";
import { metersApiSlice } from "state/meters/metersApiSlice";
import { resourcesApiSlice } from "state/resources/resourcesApiSlice";
import { contractsApiSlice } from "state/contracts/contractsApiSlice";
import { respApiSlice } from "state/resp/respApiSlice";
import { bilateralPricesApiSlice } from "state/bilateralPrices/bilateralPricesApiSlice";
import { SIMULATOR_IMPORT_VERSION } from "utils/constants";

const toArray = (normalized) => Object.values(normalized?.entities || {});

function mapResource(r) {
  const d = r.details || {};
  return {
    pre_id: r.id,
    name: r.name,
    type: r.type,
    meter: r.meter_id,
    sumCertifiedPower: d.sum_certified_power ?? 0,
    voltageLevel: d.ic_voltage_level ?? "MT",
    generationCapacity: d.generation_capacity ?? 0,
    nominalCapacity: d.nominal_capacity ?? 0,
    maxPower: d.max_power ?? 0,
    maxPowerInput: d.max_power_input ?? 0,
    maxPowerOutput: d.max_power_output ?? 0,
    power: d.power ?? 0,
    capacity: d.capacity ?? 0,
    maxTemp: d.max_temp ?? 0,
    defaultStartTemp: d.default_start_temp ?? 0,
    targetTemp: d.target_temp ?? 0,
    chargingEfficiency: d.charging_efficiency ?? 0,
    dischargingEfficiency: d.discharging_efficiency ?? 0,
    minSOC: d.min_SOC ?? 0,
    maxSOC: d.max_SOC ?? 0,
    initSoc: d.init_soc ?? 0,
    desiredMinSOC: d.desired_min_SOC ?? 0,
    degCost: d.deg_cost ?? 0,
    powerRated: d.power_rated ?? 0,
    capacityTank: d.capacity_tank ?? 0,
    tempInlet: d.temp_inlet ?? 0,
    tempDesired: d.temp_desired ?? 0,
    tempOutInit: d.temp_out_init ?? 0,
    tempIndoorInit: d.temp_indoor_init ?? 0,
    tempIndoorFinal: d.temp_indoor_final ?? 0,
    tempIndoorMin: d.temp_indoor_min ?? 0,
    tempIndoorMax: d.temp_indoor_max ?? 0,
    tempOutMin: d.temp_out_min ?? 0,
    tempOutMax: d.temp_out_max ?? 0,
    uValue: d.u_value ?? 0,
    thermalResistance: d.thermal_resistance ?? 0,
    hRad: d.h_rad ?? 0,
    areaRad: d.area_rad ?? 0,
    ownerships: (r.ownerships || []).map((o) => ({
      member: o.user_id,
      perc: o.perc_ownership,
    })),
  };
}

function stripContractPayload(payload) {
  if (!payload) return payload;
  const result = { ...payload };
  delete result.contract_id;
  return result;
}

function ExportCommunity({ community }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(["community"]);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const communityId = community.id;

      const [membersRes, metersRes, resourcesRes, respRes, bilateralRes] = await Promise.all([
        dispatch(membersApiSlice.endpoints.getMembersByCommunityId.initiate({ communityId })),
        dispatch(metersApiSlice.endpoints.getMetersByCommunityId.initiate({ communityId })),
        dispatch(resourcesApiSlice.endpoints.getResourcesByCommunityId.initiate({ communityId })),
        dispatch(respApiSlice.endpoints.getAllRespByCommunityId.initiate(communityId)),
        dispatch(
          bilateralPricesApiSlice.endpoints.getFixedBilateralPrices.initiate({ communityId })
        ),
      ]);

      const members = toArray(membersRes.data);
      const meters = toArray(metersRes.data);
      const resources = toArray(resourcesRes.data);
      const resp = toArray(respRes.data);
      const fixedBilateralPrices = bilateralRes.data || [];

      const contractsResults = await Promise.all(
        meters.map((meter) =>
          dispatch(
            contractsApiSlice.endpoints.getContractsByMeterId.initiate({
              communityId,
              meterId: meter.id,
            })
          )
        )
      );
      const contracts = contractsResults.flatMap((r) => r.data || []);

      const data = {
        import_version: SIMULATOR_IMPORT_VERSION,
        basicInfo: {
          community_name: community.name,
          community_identifier: community.identifier,
          allocation: community.method_alloc_coeff,
          community_or_aggregator_type: community.community_or_aggregator_type,
          market_rate: community.market_rate,
          market_divisor: community.market_divisor,
          market_compensation: community.market_compensation,
          market_small_increment: community.market_small_increment,
          market_fixed_price: community.market_fixed_price,
        },
        members: members.map((m) => ({
          pre_id: m.id,
          name: m.name,
          is_host: m.is_host,
          is_promoter: m.is_promoter,
        })),
        meters: meters.map((m) => ({
          pre_id: m.id,
          name: m.identifier,
          type: m.type,
          cpe: m.cpe_id || "",
          installation_type: m.installation_type,
          production_identifier: m.production_identifier || "",
        })),
        resources: resources.map(mapResource),
        contracts: contracts.map((c) => ({
          buy_type: c.buy_type,
          sell_type: c.sell_type,
          voltage_level: c.voltage_level,
          tariff_cycle: c.tariff_cycle,
          with_holidays: c.with_holidays,
          contracted_power: c.contracted_power,
          member: c.user_id,
          validity: c.validity,
          buy_payload: stripContractPayload(c.buy_payload),
          sell_payload: stripContractPayload(c.sell_payload),
        })),
        resp: resp.map((r) => ({
          provider_meter_id: r.provider_meter_id,
          receiver_meter_id: r.receiver_meter_id,
          is_active: r.is_active,
        })),
        fixedBilateralPrices: fixedBilateralPrices.map((fp) => ({
          provider_user_id: fp.provider_user_id,
          receiver_user_id: fp.receiver_user_id,
          price: fp.price,
        })),
      };

      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = community.name
        ? `community_${community.name.replace(/\s+/g, "_").toLowerCase()}.json`
        : "community.json";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SectionCard
      id="export"
      icon="download"
      title={t("export.title")}
      action={
        <Button variant="ghost" size="sm" onClick={handleExport} disabled={isExporting}>
          <Icon name={isExporting ? "hourglass_top" : "download"} />
          {isExporting ? t("export.exporting") : t("export.button")}
        </Button>
      }
    >
      <p className="text-[13px] text-ink-3 leading-[1.6] m-0 max-w-[560px]">
        {t("export.description")}
      </p>
    </SectionCard>
  );
}

ExportCommunity.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  community: PropTypes.object.isRequired,
};

export default ExportCommunity;
